/**
 * #191 quick-batch — confirmation modal for the "Back to plan"
 * escape hatch.
 *
 * Surfaces when the user clicks the Workspace header's
 * "Back to plan" button during Phase B (`interview.subPhase === 'build'`).
 * Per the spec, the modal MUST require a click — no hover / press-and-hold
 * shortcut — because pausing the build is destructive enough that
 * accidental clicks would be costly.
 *
 * The visible Cancel button + Escape key + backdrop click all close
 * the modal without firing the confirm callback. Only the explicit
 * "Pause build" CTA fires `onConfirm`.
 */

import { useEffect, useRef, type ReactElement } from 'react';

export const BACK_TO_PLAN_MODAL_TESTID = 'back-to-plan-modal';
export const BACK_TO_PLAN_CONFIRM_TESTID = 'back-to-plan-modal-confirm';
export const BACK_TO_PLAN_CANCEL_TESTID = 'back-to-plan-modal-cancel';

export interface BackToPlanConfirmModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function BackToPlanConfirmModal({
  open,
  onCancel,
  onConfirm,
}: BackToPlanConfirmModalProps): ReactElement | null {
  // Cancel button gets initial focus on open. Codex round-1 MEDIUM-3:
  // moving focus to the SAFE (cancel) action — rather than the
  // destructive confirm — reduces the chance a stray Enter
  // immediately pauses the build. The trigger element's previous
  // focus is restored on close so keyboard users can resume where
  // they left off.
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Escape closes the modal — counts as cancel, NOT confirm. Per the
  // spec, the confirm path requires an explicit click on the
  // "Pause build" button.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onCancel]);

  // Codex round-1 MEDIUM-3: focus management for screen readers /
  // keyboard users. On open, capture the previously focused element,
  // move focus into the dialog (cancel button), and restore the
  // original focus on close. Skipped during SSR (document undefined).
  useEffect(() => {
    if (!open) return;
    if (typeof document === 'undefined') return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // queueMicrotask so the ref is attached before we read it.
    queueMicrotask(() => {
      cancelButtonRef.current?.focus();
    });
    return () => {
      previouslyFocused?.focus?.();
    };
  }, [open]);

  // Codex round-1 MEDIUM-3: simple Tab/Shift+Tab focus trap. We
  // bind on the dialog wrapper (rather than `window`) so the
  // background controls outside the dialog can't tab-receive focus.
  // For a two-button modal this lightweight trap is sufficient;
  // a richer dialog would warrant a dedicated focus-trap library.
  useEffect(() => {
    if (!open) return;
    if (typeof document === 'undefined') return;
    const trap = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusables = dialog.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !dialog.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !dialog.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', trap);
    return () => window.removeEventListener('keydown', trap);
  }, [open]);

  if (!open) return null;

  return (
    <div
      // Codex review #4 MEDIUM (PR #646 / #643) — bumped from z-50
      // to z-[60] so this confirm modal beats the preview-recovery
      // toast (z-[55]). See DependencyApprovalModal for the full
      // rationale.
      //
      // Issue #965 — `app-region-no-drag` defence-in-depth. The
      // Workspace's page-level `<header>` underneath this modal
      // carries `-webkit-app-region: drag` via the `app-region-drag`
      // class (see globals.css + #639 + #900). Because drag-region
      // hit-testing happens at the OS compositor level INDEPENDENTLY
      // of visual z-index, the header strip's drag region would
      // otherwise bleed through the modal backdrop and intercept the
      // backdrop click-to-cancel gesture at the screen coordinates
      // that overlap the underlying header. Mirrors the PreviewOverlay
      // pattern from PR #847 and the UpdateTemplateModal /
      // CreateGameDialog / SsoLoginModal carve-outs added in PR #900.
      className="app-region-no-drag fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onCancel}
      data-testid={BACK_TO_PLAN_MODAL_TESTID}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="back-to-plan-modal-title"
        aria-describedby="back-to-plan-modal-body"
        className="w-full max-w-md rounded-xl border border-white/10 bg-crucible-surface p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="back-to-plan-modal-title" className="mb-2 text-lg font-semibold text-white">
          Pause the build?
        </h2>
        <p
          id="back-to-plan-modal-body"
          className="mb-5 text-sm text-white/60"
        >
          Specialists already completed will keep their work. You can refine the plan and
          re-start building when you&apos;re ready.
        </p>
        <div className="flex justify-end gap-3">
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onCancel}
            data-testid={BACK_TO_PLAN_CANCEL_TESTID}
            className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/60 transition-colors hover:border-white/20 hover:text-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
          >
            Keep building
          </button>
          <button
            type="button"
            onClick={onConfirm}
            data-testid={BACK_TO_PLAN_CONFIRM_TESTID}
            className="rounded-lg bg-amber-500/90 px-4 py-2 text-sm font-medium text-white transition-opacity hover:bg-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
          >
            Pause build
          </button>
        </div>
      </div>
    </div>
  );
}
